import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImageField } from './ProductImageField';
import { useCategories } from '../hooks/useCategories';

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B] focus:border-transparent';

export const ProductForm = ({ initial, isNew, saving, onSave, onClose }) => {
  const [form, setForm] = useState(initial);
  const categories = useCategories();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) {
      toast.error('Name and price are required');
      return;
    }
    onSave({
      name: form.name,
      category: form.category,
      price: parseFloat(form.price),
      description: form.description,
      image: form.image,
      material: form.material,
      colors: form.colors.split(',').map((c) => c.trim()).filter(Boolean),
      isNew: form.isNew,
      isBestSeller: form.isBestSeller,
      gallery: form.image ? [form.image] : [],
      stock: parseInt(form.stock, 10) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-serif font-bold text-[#001B2A]">
            {isNew ? 'Add Product' : 'Edit Product'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <input data-testid="product-form-name" name="name" value={form.name} onChange={handleChange} placeholder="Product Name *" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <select name="category" value={form.category} onChange={handleChange} className={inputCls} data-testid="product-form-category">
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <input data-testid="product-form-price" name="price" type="number" step="0.01" min="0" value={form.price} onChange={handleChange} placeholder="Price *" className={inputCls} />
          </div>
          <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Description" className={`${inputCls} resize-none`} data-testid="product-form-description" />

          <ProductImageField image={form.image} onImageChange={(image) => setForm((f) => ({ ...f, image }))} />

          <div className="grid grid-cols-2 gap-3">
            <input name="material" value={form.material} onChange={handleChange} placeholder="Material" className={inputCls} />
            <input name="colors" value={form.colors} onChange={handleChange} placeholder="Colors (comma-separated)" className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Stock (quantity in inventory)</label>
            <input data-testid="product-form-stock" name="stock" type="number" min="0" step="1" value={form.stock} onChange={handleChange} placeholder="Stock" className={inputCls} />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="isNew" checked={form.isNew} onChange={handleChange} className="accent-[#7B1E2B]" />
              New Arrival
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" name="isBestSeller" checked={form.isBestSeller} onChange={handleChange} className="accent-[#7B1E2B]" />
              Best Seller
            </label>
          </div>
          <button
            type="submit"
            data-testid="product-form-save"
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-[#7B1E2B] text-white py-3 rounded-md font-semibold hover:bg-[#001B2A] transition-colors disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isNew ? 'Add Product' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};
